import * as go from 'gojs';
import 'lib/gojs';
import type {
  DomainGroup,
  ModelMapResponse,
  NodeKind,
  SlotInputNode,
} from 'types/packages-api/slot-valuation-inputs';
import { NODE_KIND_COLORS, NODE_KIND_FALLBACK_COLOR } from './utils';
import {
  buildModelMapData,
  DIMMED_LINK_OPACITY,
  nodeOpacity,
  type ModelMapNodeDatum,
} from './model-map-data';

export type { ModelMapNodeDatum } from './model-map-data';

/** The imperative handle {@link initModelMapDiagram} returns, for the React wrapper. */
export interface ModelMapDiagramHandle {
  zoomIn: () => void;
  zoomOut: () => void;
  zoomToFit: () => void;
  clearHighlight: () => void;
  dispose: () => void;
}

const $ = go.GraphObject.make;
const NODE_MAX_WIDTH = 150;

/** Dash pattern for a node that resolved to no value. See {@link ModelMapNodeDatum}. */
const UNRESOLVED_DASH = [4, 3];

// ── SOURCE-PARENT COLOURS (INS-11965) ────────────────────────────────────────────────────
// A source parent (Volume Source, AFE) is drawn as a purple box — Carbon purple-70 on
// purple-90, white text — so it reads as "where these pieces came from", matching the
// scenario parents in the mock. See ModelMapNodeDatum.isSourceParent.
const SOURCE_PARENT_COLORS = {
  fill: '#6929c4',
  stroke: '#491d8b',
  text: '#ffffff',
};

/**
 * The floor the opening fit is clamped to.
 *
 * ── AC 9 SAYS FIT ON OPEN; FIT ALONE PUT IT AT 30% WHERE NOTHING IS READABLE ──────────
 *
 * The graph is 8139 x 2546 and the viewport is a modal, so `zoomToFit` settled at 0.295.
 * At that scale the 9px formula text renders under 3px — present, and useless. Every
 * viewer had to zoom in by hand before the map said anything, which is the opposite of
 * what AC 5 and AC 6 were built for.
 *
 * CLAMPED RATHER THAN REMOVED, and the fit still runs: a graph that fits at or above this
 * scale is framed exactly as before, so AC 9 is satisfied wherever it can be. Only when
 * fitting would drop below legibility does the clamp take over and the graph scroll
 * instead — the ordinary way a large diagram behaves.
 *
 * 1.0 because the formula TextBlock is 9px, so this is the largest scale-down that still
 * clears the ~9px legibility floor. Not a round number chosen for tidiness: raise the font
 * and this can come down, which is the trade to make if opening at 100% proves too tight.
 *
 * The Fit-to-view BUTTON is deliberately NOT clamped — pressing it means "show me
 * everything", and a control that refuses to do what it says is worse than a small font.
 */
export const MIN_OPEN_SCALE = 1;

/** Breathing room left around the graph's top-left corner when the clamp applies. */
const OPEN_MARGIN = 24;

/**
 * The scale the map OPENS at: the fit scale, but never below MIN_OPEN_SCALE.
 *
 * Extracted and exported so the "opened at 29%, where the 9px formula text is 3px and nothing
 * is legible" decision has EXECUTING coverage without the `canvas` native module a real GoJS
 * diagram needs — model-map-render.test.ts is skipped in CI, so the clamp applied inline in the
 * fitTimer below had none. The Fit-to-view BUTTON deliberately does NOT clamp (see
 * MIN_OPEN_SCALE); only the automatic opening fit does.
 *
 * @param {number} fitScale - The scale zoomToFit settled on.
 * @return {number} The clamped opening scale.
 */
export function openingScale(fitScale: number): number {
  return Math.max(fitScale, MIN_OPEN_SCALE);
}

/**
 * Colour triple for a node kind, falling back to neutral grey for a kind not in
 * the palette (kinds are an open set server-side).
 *
 * @param {NodeKind} kind - The node's kind.
 * @return {object} The colour triple (`{ fill, stroke, text }`).
 */
function colorsFor(kind: NodeKind) {
  return NODE_KIND_COLORS[kind] ?? NODE_KIND_FALLBACK_COLOR;
}

/**
 * One AC 6 value line beneath a function node's label — the input's label and its
 * value, formatted by the same helper the table uses.
 */
const VALUE_LINE_TEMPLATE = $(
  go.Panel,
  'Auto',
  $(
    go.TextBlock,
    {
      font: '400 10px "IBM Plex Sans", sans-serif',
      stroke: '#525252',
      textAlign: 'left',
      wrap: go.TextBlock.WrapFit,
      maxSize: new go.Size(NODE_MAX_WIDTH, NaN),
    },
    new go.Binding('text', 'text')
  )
);

/**
 * Dims every node/link except the selected node and its full upstream ancestry
 * (BFS through incoming links), so a click reveals what feeds the clicked node.
 *
 * Writes only `dimmed`. AC 4's category fading lives on a SEPARATE field so that
 * {@link resetHighlight} — which clears `dimmed` unconditionally — cannot un-fade it.
 *
 * @param {go.Diagram} diagram - The live diagram.
 * @param {go.Node} selected - The clicked node.
 */
function highlightAncestors(diagram: go.Diagram, selected: go.Node): void {
  diagram.startTransaction('highlight');
  const ancestors = new Set<go.Key>([selected.key]);
  const queue: go.Node[] = [selected];
  while (queue.length > 0) {
    const node = queue.shift()!;
    node.findNodesInto().each((pred) => {
      if (!ancestors.has(pred.key)) {
        ancestors.add(pred.key);
        queue.push(pred);
      }
    });
  }
  diagram.nodes.each((n) =>
    diagram.model.setDataProperty(n.data, 'dimmed', !ancestors.has(n.key))
  );
  diagram.links.each((lk) => {
    const lit =
      ancestors.has(lk.fromNode?.key ?? '') &&
      ancestors.has(lk.toNode?.key ?? '');
    diagram.model.setDataProperty(lk.data, 'dimmed', !lit);
  });
  diagram.commitTransaction('highlight');
}

/**
 * Clears any highlight — every node/link back off the transient dimming.
 *
 * A faded node returns to {@link nodeOpacity}'s faded value, NOT to full opacity: this
 * writes `dimmed`, and `faded` is a different field.
 *
 * @param {go.Diagram} diagram - The live diagram.
 */
function resetHighlight(diagram: go.Diagram): void {
  diagram.startTransaction('clear');
  diagram.nodes.each((n) =>
    diagram.model.setDataProperty(n.data, 'dimmed', false)
  );
  diagram.links.each((lk) =>
    diagram.model.setDataProperty(lk.data, 'dimmed', false)
  );
  diagram.commitTransaction('clear');
}

/**
 * Builds the GoJS diagram for the slot-scoped model map from the `model_map`
 * response, into `div`. The registry supplies the graph; the diagram supplies the
 * layout (a top-down `LayeredDigraphLayout` over the edges — the map's own node positions are
 * not in the payload). Zone exclusion, edge pruning, the source / scenario parents, AC 5's
 * formula text, AC 6's value lines and AC 4's fading are all decided in
 * {@link buildModelMapData}, which is pure and tested; this function only binds the result.
 *
 * A node stacks label, then formula (AC 5), then value lines (AC 6, which words itself as
 * "beneath the formula").
 *
 * Nodes colour by `kind`; a click highlights a node's upstream ancestry and calls
 * `onSelect`; a background click clears it.
 *
 * @param {HTMLDivElement} div - The mounted, sized container.
 * @param {ModelMapResponse} response - The registry map (zones / nodes / edges).
 * @param {Function} onSelect - Selection callback `(node | null) => void` (null on clear).
 * @param {SlotInputNode[]} [slotInputs] - The slot's resolved inputs, when loaded.
 * @param {(string|null)} [slotCategory] - The slot's reserve category, when resolved.
 * @param {DomainGroup[]} [domains] - The payload's per-domain groups, for the scenario parents.
 * @return {ModelMapDiagramHandle} Imperative zoom / clear / dispose controls.
 */
/**
 * ── THE ZOOM BUTTONS (UAT 13, 9 Sep) ────────────────────────────────────────────────────
 *
 * Andrew reported they do nothing. They were never unwired: they call
 * `commandHandler.increaseZoom()` / `decreaseZoom()`, GoJS's real commands, and those are
 * not blocked here — minScale/maxScale are the library defaults (0.0001 and 100) and the map
 * opens clamped to MIN_OPEN_SCALE, so a zoom is always inside the permitted range.
 *
 * TWO THINGS MADE THAT INVISIBLE, and both are fixed because both are real:
 *
 *   THE STEP. `zoomFactor` defaults to 1.05 — 5% per click (go.d.ts:14046). The graph is
 *   8139 x 2546 in a modal viewport, so at the opening scale it already overflows by
 *   several times; 5% shifts the visible window by less than one node's width. The command
 *   ran, the scale changed, and nothing looked different. ZOOM_STEP replaces it.
 *
 *   THE FIT RACE. `fitTimer` fires 200 ms after init and calls zoomToFit() plus the clamp.
 *   A click inside that window is genuinely undone — the user zooms, the timer re-frames,
 *   and the zoom is gone. Cancelled on the first user zoom, which is correct whether or not
 *   it is what Andrew hit: an automatic re-frame must never overwrite a deliberate action.
 *
 * WHAT COULD NOT BE CONFIRMED HERE: which of the two dominated in Andrew's session. Ranking
 * them needs a browser — the repo's only real-GoJS test is skipped unless `canvas` is
 * installed, and "is 5% visible" is a perceptual question a jsdom assertion cannot answer.
 * Both are defects on their own terms, so both are fixed and the report says so.
 */

/**
 * Scale multiplier per zoom click.
 *
 * 1.25 (25%), not GoJS's 1.05. Chosen so ONE click is unmistakable on a graph that overflows
 * its viewport, while still taking three clicks to roughly double — a 2.0 step would make the
 * buttons unusable for framing a region. It matches the 1.2–1.25 that general-purpose zoom
 * controls settle on.
 */
export const ZOOM_STEP = 1.25;

/** The three zoom commands the toolbar calls. */
export interface ModelMapZoomCommands {
  zoomIn: () => void;
  zoomOut: () => void;
  zoomToFit: () => void;
}

/** The subset of a GoJS Diagram these commands touch, so they can be exercised without one. */
export interface ZoomableDiagram {
  commandHandler: {
    increaseZoom: (factor?: number) => void;
    decreaseZoom: (factor?: number) => void;
  };
  zoomToFit: () => void;
}

/**
 * The three zoom commands, with the explicit step and the fit-race cancellation.
 *
 * Extracted and exported so its behaviour is testable: initialising a real GoJS diagram needs
 * the `canvas` native module, which is deliberately not a dependency of this repo, so a test
 * that went through initModelMapDiagram would be skipped in CI and prove nothing. This takes
 * the narrowest interface it needs and a plain fake satisfies it.
 *
 * @param {ZoomableDiagram} diagram - The diagram to zoom.
 * @param {Function} cancelAutoFit - Cancels the pending fit so it cannot undo a user zoom.
 * @return {ModelMapZoomCommands} zoomIn / zoomOut / zoomToFit.
 */
export function makeZoomCommands(
  diagram: ZoomableDiagram,
  cancelAutoFit: () => void
): ModelMapZoomCommands {
  return {
    zoomIn: () => {
      cancelAutoFit();
      diagram.commandHandler.increaseZoom(ZOOM_STEP);
    },
    zoomOut: () => {
      cancelAutoFit();
      diagram.commandHandler.decreaseZoom(1 / ZOOM_STEP);
    },
    // Fit is the user ASKING to re-frame, so the pending automatic fit is redundant rather
    // than harmful — cancelled anyway, so one code path owns the timer.
    zoomToFit: () => {
      cancelAutoFit();
      diagram.zoomToFit();
    },
  };
}

export function initModelMapDiagram(
  div: HTMLDivElement,
  response: ModelMapResponse,
  onSelect: (node: ModelMapNodeDatum | null) => void,
  slotInputs: SlotInputNode[] = [],
  slotCategory: string | null = null,
  domains: DomainGroup[] = []
): ModelMapDiagramHandle {
  const diagram = $(go.Diagram, div, {
    'undoManager.isEnabled': false,
    'allowMove': false,
    'allowCopy': false,
    'allowDelete': false,
    'animationManager.isEnabled': true,
    'padding': new go.Margin(24, 32, 32, 32),
    // One top-down funnel, matching the mock: the source / scenario parents have only outgoing
    // edges, so they settle into the top layer and fan DOWN into the pieces they feed, and the
    // whole registry converges to the cashflow / NPV outputs at the bottom.
    'layout': $(go.LayeredDigraphLayout, {
      direction: 90, // top-to-bottom
      layerSpacing: 44,
      columnSpacing: 22,
      setsPortSpots: false,
    }),
    'click': () => {
      resetHighlight(diagram);
      onSelect(null);
    },
  });

  diagram.nodeTemplate = $(
    go.Node,
    'Spot',
    {
      selectionAdorned: false,
      cursor: 'pointer',
      locationSpot: go.Spot.Center,
      fromSpot: go.Spot.Bottom,
      toSpot: go.Spot.Top,
      click: (_e: go.InputEvent, obj: go.GraphObject) => {
        const node = obj.part as go.Node;
        highlightAncestors(diagram, node);
        onSelect(node.data as ModelMapNodeDatum);
      },
    },
    // Bound to the WHOLE datum, not to `dimmed`, so the transient highlight and the
    // permanent category fading compose instead of overwriting each other. GoJS
    // re-evaluates an empty-sourceName binding on every setDataProperty, so clearing a
    // highlight still repaints — at the faded opacity, which is the point.
    new go.Binding('opacity', '', nodeOpacity),
    $(
      go.Panel,
      'Auto',
      $(
        go.Shape,
        'RoundedRectangle',
        { parameter1: 4, strokeWidth: 1.5 },
        // A source parent (Volume Source, AFE) is purple; every other node colours by kind.
        new go.Binding('fill', '', (d: ModelMapNodeDatum) => {
          return d.isSourceParent
            ? SOURCE_PARENT_COLORS.fill
            : colorsFor(d.kind).fill;
        }),
        new go.Binding('stroke', '', (d: ModelMapNodeDatum) => {
          return d.isSourceParent
            ? SOURCE_PARENT_COLORS.stroke
            : colorsFor(d.kind).stroke;
        }),
        // "No value" rides on the STROKE, never on opacity — a faded node is present and
        // unselected, an unresolved one has nothing to show, and the two must not read
        // the same. Different channel, no ambiguous combination.
        new go.Binding('strokeDashArray', 'unresolved', (u: boolean) => {
          return u ? UNRESOLVED_DASH : null;
        })
      ),
      $(
        go.Panel,
        'Vertical',
        { margin: new go.Margin(6, 9) },
        $(
          go.TextBlock,
          {
            font: '600 11px "IBM Plex Sans", sans-serif',
            textAlign: 'center',
            wrap: go.TextBlock.WrapFit,
            maxSize: new go.Size(NODE_MAX_WIDTH, NaN),
          },
          new go.Binding('text', 'label'),
          new go.Binding('stroke', '', (d: ModelMapNodeDatum) => {
            return d.isSourceParent
              ? SOURCE_PARENT_COLORS.text
              : colorsFor(d.kind).text;
          })
        ),
        // AC 5. `visible` is bound rather than the text alone, so a node with no formula
        // draws NO ELEMENT — an invisible GraphObject takes no space in a Vertical panel.
        // Rendering an empty TextBlock instead would put an empty box where a formula
        // would be on all 48 prose-only nodes, which is the case AC 5 calls out.
        $(
          go.TextBlock,
          {
            // 11px, matching the LABEL, not smaller. At the clamped opening scale of 1.0
            // a 9px font measured 7.04px of line box — under the ~9px legibility floor —
            // and shrinking the formula to fit more graph in defeats the point of drawing
            // it. The monospace face and the grey already distinguish it from the label.
            font: '400 11px "IBM Plex Mono", ui-monospace, monospace',
            stroke: '#525252',
            textAlign: 'left',
            wrap: go.TextBlock.WrapFit,
            maxSize: new go.Size(NODE_MAX_WIDTH, NaN),
            margin: new go.Margin(4, 0, 0, 0),
          },
          new go.Binding('text', 'formulaText'),
          new go.Binding('visible', 'formulaText', (f: string | null) => {
            return f !== null;
          })
        ),
        // AC 6, BENEATH THE FORMULA as the criterion words it. Empty on every non-function
        // node, so the panel collapses and those nodes keep exactly the height they had
        // before this existed.
        $(
          go.Panel,
          'Vertical',
          {
            itemTemplate: VALUE_LINE_TEMPLATE,
            alignment: go.Spot.Left,
            alignmentFocus: go.Spot.Left,
            margin: new go.Margin(3, 0, 0, 0),
          },
          new go.Binding('itemArray', 'valueLines')
        )
      )
    )
  );

  diagram.linkTemplate = $(
    go.Link,
    {
      routing: go.Link.AvoidsNodes,
      corner: 4,
      selectionAdorned: false,
      layerName: 'Background',
    },
    new go.Binding('opacity', 'dimmed', (d: boolean) => {
      return d ? DIMMED_LINK_OPACITY : 1;
    }),
    $(go.Shape, { stroke: '#a8a8a8', strokeWidth: 1.5 }),
    $(go.Shape, { toArrow: 'OpenTriangle', stroke: '#a8a8a8' })
  );

  const { nodeData, linkData, scenarioNodes, scenarioLinks } =
    buildModelMapData(response, slotInputs, slotCategory, domains);

  // The scenario parents ride in the node array beside the registry nodes (they carry
  // `isSourceParent`, so the node template paints them purple), and their presentation links
  // join the registry and source-parent links.
  diagram.model = new go.GraphLinksModel(
    [...nodeData, ...scenarioNodes],
    [...linkData, ...scenarioLinks]
  );

  // Frame the map once the layout settles, but never below readability.
  const fitTimer = window.setTimeout(() => {
    // `finally`, not `catch`: the clamp must run even if the fit's repaint throws, and any
    // real error still propagates rather than being swallowed. Found because a repaint
    // failure in the render harness skipped the clamp and left the map at 29% — the exact
    // state this exists to prevent, reachable through an unrelated failure.
    try {
      diagram.zoomToFit();
    } finally {
      const clamped = openingScale(diagram.scale);
      if (clamped !== diagram.scale) {
        diagram.scale = clamped;
        // Anchor to the graph's top-left rather than leaving it centred on empty canvas:
        // the layout is top-down, so the roots are what a reader wants to start from.
        diagram.position = new go.Point(
          diagram.documentBounds.x - OPEN_MARGIN,
          diagram.documentBounds.y - OPEN_MARGIN
        );
      }
    }
  }, 200);

  const zoom = makeZoomCommands(diagram, () => window.clearTimeout(fitTimer));

  return {
    zoomIn: zoom.zoomIn,
    zoomOut: zoom.zoomOut,
    zoomToFit: zoom.zoomToFit,
    clearHighlight: () => {
      resetHighlight(diagram);
      onSelect(null);
    },
    dispose: () => {
      window.clearTimeout(fitTimer);
      // Detach from the DOM node so GoJS releases it (the typing wants an
      // HTMLDivElement, so cast — mirrors the title-chain-validator viewer).
      diagram.div = null as unknown as HTMLDivElement;
    },
  };
}
